import { useTheme } from '@emotion/react';
import {
  AudioFile,
  Delete,
  Download,
  Excel,
  FileGif,
  FileJpg,
  FilePdfOne,
  FileSuccess,
  FileTxt,
  FileWord,
  LoadingOne,
  Powerpoint,
} from '@icon-park/react';
import type { Icon } from '@icon-park/react/lib/runtime';
import { memo } from 'react';
import { useIntl } from 'react-intl';

import { Truncate } from '@didaskill/ui/utils';

import { Button } from '../../general/button/Button';
import { ICON_SIZES } from '../../general/icon/constants';
import { Typography } from '../../general/typography/Typography';
import messages from './FileBox.messages';
import * as styles from './FileBox.styles';

export const MIME_TYPE_ICON_MAPPING: Record<string, Icon> = {
  'application/pdf': FilePdfOne,
  'image/jpeg': FileJpg,
  'image/gif': FileGif,
  'text/plain': FileTxt,
  'application/msword': FileWord,
  'application/x-cfb': FileWord,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': FileWord,
  'application/vnd.ms-powerpoint': Powerpoint,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': Powerpoint,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': Excel,
  'application/vnd.ms-excel': Excel,
  'audio/mpeg': AudioFile,
  'audio/MPA': AudioFile,
  'audio/mpa-robust': AudioFile,
  'audio/wav': AudioFile,
  'audio/vnd.wave': AudioFile,
  'audio/x-wav': AudioFile,
  'audio/wave': AudioFile,
};

export type FileBoxProps = {
  /** File name. */
  fileName: string;
  /** File download link. */
  href?: string;
  /** Icon displayed instead of the mime-type default. */
  icon?: Icon;
  /** File mimeType. */
  mimeType: string;
  /** Is the remove button disabled. */
  isDisabled?: boolean;
  /** Display faded download links not to catch user attention. */
  isFaded?: boolean;
  /** Display loader if file is loading */
  isLoading?: boolean;
  /** Remove file */
  onRemove?: () => void;
  /** Download file */
  onDownload?: () => void;
  /** Is the file downloading */
  isDownloading?: boolean;
};

export const FileBoxBase = ({
  fileName,
  mimeType,
  icon,
  isDisabled = false,
  isFaded = false,
  isLoading = false,
  onRemove,
  onDownload,
  isDownloading = false,
}: FileBoxProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const IconElement = icon ?? MIME_TYPE_ICON_MAPPING[mimeType] ?? FileSuccess;

  const disabledButtonTheme = [theme.colors.gray[500], theme.colors.gray[25]];

  return (
    <div css={styles.container}>
      <div css={styles.iconContainer}>
        <IconElement
          size={ICON_SIZES.LARGE}
          fill={[
            isDisabled || isFaded ? theme.colors.primary[300] : theme.colors.primary[500],
            theme.colors.primary[15],
          ]}
        />
      </div>

      <div css={styles.fileContainer}>
        <Truncate css={styles.fileName} maxLines={1}>
          <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}>{fileName}</Typography>
        </Truncate>

        <div css={styles.buttonsContainer}>
          {!!onDownload && (
            <Button
              aria-label={intl.formatMessage(messages.DOWNLOAD_FILE)}
              icon={
                isLoading || isDownloading ? (
                  <LoadingOne css={styles.spinner} />
                ) : (
                  <Download
                    fill={
                      isDisabled
                        ? disabledButtonTheme
                        : [theme.colors.primary[500], theme.colors.primary[25]]
                    }
                  />
                )
              }
              onClick={onDownload}
              disabled={isDisabled}
            />
          )}

          {!!onRemove && (
            <Button
              aria-label={intl.formatMessage(messages.REMOVE_FILE)}
              icon={
                <Delete
                  fill={
                    isDisabled
                      ? disabledButtonTheme
                      : [theme.colors.danger[500], theme.colors.danger[25]]
                  }
                />
              }
              onClick={onRemove}
              disabled={isDisabled}
            />
          )}
        </div>
      </div>
    </div>
  );
};

FileBoxBase.displayName = 'FileBox';

export const FileBox = memo(FileBoxBase);
